import React from "react";
import cn from "./helpers/cn";

type FormControlContextValue = {
  variant: "standard" | "outlined" | "filled";
  size: "small" | "medium";
  fullWidth: boolean;
  error: boolean;
  disabled: boolean;
  required: boolean;
  focused: boolean;
  color: "primary" | "secondary";
  margin: "none" | "dense" | "normal";
  hiddenLabel: boolean;
  onFocus: () => void;
  onBlur: () => void;
};

export const FormControlContext = React.createContext<FormControlContextValue | null>(null);

export function useFormControl(): FormControlContextValue | null {
  return React.useContext(FormControlContext);
}

interface FormControlProps extends React.HTMLAttributes<HTMLDivElement> {
  component?: React.ElementType;
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  fullWidth?: boolean;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  focused?: boolean;
  color?: "primary" | "secondary";
  margin?: "none" | "dense" | "normal";
  hiddenLabel?: boolean;
  children?: React.ReactNode;
}

const marginMap = {
  none: "",
  dense: "mt-1 mb-1",
  normal: "mt-4 mb-2",
} as const;

const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>((props, ref) => {
  const {
    className,
    component: Component = "div",
    variant = "outlined",
    size = "medium",
    fullWidth = false,
    error = false,
    disabled = false,
    required = false,
    focused: focusedProp,
    color = "primary",
    margin = "none",
    hiddenLabel = false,
    children,
    ...other
  } = props;

  const [focusedState, setFocusedState] = React.useState(false);
  const focused = focusedProp ?? focusedState;

  const ctx = React.useMemo<FormControlContextValue>(() => ({
    variant,
    size,
    fullWidth,
    error,
    disabled,
    required,
    focused,
    color,
    margin,
    hiddenLabel,
    onFocus: () => setFocusedState(true),
    onBlur: () => setFocusedState(false),
  }), [variant, size, fullWidth, error, disabled, required, focused, color, margin, hiddenLabel]);

  return (
    <FormControlContext.Provider value={ctx}>
      <Component
        ref={ref}
        className={cn(
          "inline-flex flex-col relative min-w-0 p-0 m-0 border-0 align-top",
          fullWidth && "w-full",
          marginMap[margin],
          className
        )}
        {...other}
      >
        {children}
      </Component>
    </FormControlContext.Provider>
  );
});

FormControl.displayName = "FormControl";

export default FormControl;
